import { stableHash } from '../hash.js';
import { registerTriggerSource } from '../registry.js';
import type {
  TriggerEvent,
  TriggerSourceAdapter,
  TriggerSubscription,
  TriggerWebhookRequest,
} from '../types.js';

/**
 * Firecrawl monitoring source adapter — the first webhook-kind trigger.
 * Ported from PR #1581's webhooks-firecrawl.ts (absorbed into the Triggers
 * spine before that PR merged; `formatFirecrawlEvent` is its formatter,
 * verbatim in behaviour, with the route's filter/no-op semantics expressed as
 * the envelope's `meaningful` flag).
 *
 * Firecrawl's monitoring feature (https://docs.firecrawl.dev/features/monitoring)
 * fires a webhook on every scheduled check of a watched URL/site:
 *   - `monitor.page`            — one per page; per-page change status + diff.
 *   - `monitor.check.completed` — one per check once all pages reconcile.
 */

/** Per-page change statuses Firecrawl reports; we only notify on real changes. */
const NOTIFIABLE_STATUSES = new Set(['new', 'changed', 'removed']);

interface FirecrawlPageResult {
  url?: string;
  status?: string; // same | new | changed | removed | error
  changeStatus?: string; // some payload shapes use changeStatus
  isMeaningful?: boolean;
}

export interface FirecrawlMonitorEvent {
  type?: string; // monitor.page | monitor.check.completed
  event?: string; // some payload shapes use `event` instead of `type`
  monitorId?: string;
  url?: string;
  status?: string;
  changeStatus?: string;
  isMeaningful?: boolean;
  results?: FirecrawlPageResult[];
  metadata?: Record<string, unknown>;
}

/**
 * Build the human-facing message body for a Firecrawl event. Returns null when
 * the event carries no notifiable change (the spine records it with
 * meaningful=false and acks 200 without messaging the agent — same
 * no-spam semantics as PR #1581's `{delivered: false}`).
 */
export function formatFirecrawlEvent(evt: FirecrawlMonitorEvent): string | null {
  const kind = evt.type ?? evt.event ?? 'monitor.event';
  const monitor = evt.monitorId ? ` (monitor \`${evt.monitorId}\`)` : '';

  if (kind === 'monitor.check.completed') {
    const results = Array.isArray(evt.results) ? evt.results : [];
    const changed = results.filter(
      (r) =>
        // Honour per-page meaningful-change judging, same as the single-page
        // branch below — a page Firecrawl judged non-meaningful isn't notifiable
        // even if its status is in NOTIFIABLE_STATUSES.
        r.isMeaningful !== false &&
        NOTIFIABLE_STATUSES.has((r.status ?? r.changeStatus ?? '').toLowerCase()),
    );
    if (changed.length === 0) return null;
    const lines = changed
      .slice(0, 25)
      .map((r) => `- ${(r.status ?? r.changeStatus ?? 'changed').toLowerCase()}: ${r.url ?? '(unknown url)'}`);
    const more = changed.length > lines.length ? `\n…and ${changed.length - lines.length} more.` : '';
    return `🔥 Firecrawl monitor detected ${changed.length} changed page(s)${monitor}:\n${lines.join('\n')}${more}`;
  }

  // monitor.page (and any single-page shape). Fail closed on shapes that
  // don't clearly look like a page event — an underspecified body (e.g. `{}`)
  // must not synthesize a false "page changed" alert (CodeRabbit, S1 review).
  if (
    evt.url === undefined &&
    evt.status === undefined &&
    evt.changeStatus === undefined &&
    evt.isMeaningful === undefined
  ) {
    return null;
  }
  const status = (evt.status ?? evt.changeStatus ?? '').toLowerCase();
  if (status && !NOTIFIABLE_STATUSES.has(status)) return null;
  // If meaningful-change judging ran and said this isn't meaningful, skip it.
  if (evt.isMeaningful === false) return null;
  const url = evt.url ?? '(unknown url)';
  const label = status || 'changed';
  return `🔥 Firecrawl monitor: page ${label}${monitor}\n${url}`;
}

export const firecrawlTriggerAdapter: TriggerSourceAdapter = {
  provider: 'firecrawl',
  kind: 'webhook',
  webhookAuth: 'bearer',
  webhookEvents: ['monitor.check.completed', 'monitor.page'],

  ingest(req: TriggerWebhookRequest, _trigger: TriggerSubscription): TriggerEvent[] {
    const evt = (req.body ?? {}) as FirecrawlMonitorEvent;
    const kind = evt.type ?? evt.event ?? 'monitor.event';
    const content = formatFirecrawlEvent(evt);

    // Dedup-key trade-off (council-reviewed): Firecrawl events carry no stable
    // event id, so we hash the full payload. That collapses webhook RETRIES of
    // the same event (the goal); if Firecrawl ever emits a byte-identical
    // payload for a genuinely new check, that duplicate notification is
    // suppressed too — acceptable for change monitoring, revisit if Firecrawl
    // adds an event id.
    const dedupKey = `fc:${stableHash(JSON.stringify(req.body ?? null))}`;

    return [
      {
        provider: 'firecrawl',
        occurredAt: new Date().toISOString(), // payload carries no event timestamp
        dedupKey,
        sourceTrust: 'untrusted',
        title:
          content === null
            ? `${kind}: no notifiable change`
            : evt.monitorId
              ? `${kind} (monitor ${evt.monitorId})`
              : kind,
        body: content ?? '',
        raw: req.body,
        meaningful: content !== null,
      },
    ];
  },
};

registerTriggerSource(firecrawlTriggerAdapter);
